import { defineStore } from "pinia";
import type { LLMSettings } from "../../models";
import type { LLMModel } from "../../api/llm";
import { DEFAULT_CHUNK_NUMBER, DEFAULT_CHUNK_SIZE, DEFAULT_OVERLAP, DEFAULT_PROMPT_CHAT } from "../../utils";

/** Default chat settings, mirroring the initial `settings` in LLM.vue. */
function defaultSettings(): LLMSettings {
  return {
    temperature: 0.15,
    systemPrompt: DEFAULT_PROMPT_CHAT,
    retrievalPrompt: "",
    chunkSize: DEFAULT_CHUNK_SIZE,
    chunkNumber: DEFAULT_CHUNK_NUMBER,
    appendChunkNumber: 0,
    chunkOverlap: DEFAULT_OVERLAP,
    modelName: "",
    useHistory: true,
    useVision: false,
    inputMode: "chat",
    activeTab: "main",
    model: null,
    thinkingMode: null,
    websearchSettings: {
      includeDomains: [],
      excludeDomains: [],
      startDate: null,
      endDate: null,
      fetchN: 5,
      searchDepth: "standard",
    },
    hybrid: false,
    fusion: "rrf",
    denseWeight: 0.5,
    expandQuery: false,
    imageParams: {},
  } as unknown as LLMSettings;
}

/**
 * Shared state for the start-page chat composer (CommandBar). Holds the model
 * list + chosen model/settings so the home bar mirrors the LLM composer, and a
 * `pendingPrompt` that LLM.vue picks up on mount to start the chat immediately.
 */
export const useComposerStore = defineStore("composer", {
  state: () => ({
    models: [] as { label: string; options: LLMModel[] }[],
    settings: defaultSettings(),
    useWebSearch: false,
    pendingPrompt: null as string | null,
    // Set by the Home "Bild generieren" tile: LLM.vue picks this up on mount to
    // open a fresh chat with an image model preselected (image_generation mode).
    startImageGeneration: false,
    // Set by the Home command-bar paperclip ("Chat mit Dokument"): LLM.vue picks
    // this up (on mount and via watcher) to open the document upload flow.
    startDocumentChat: false,
    // Set by the Home drag & drop: LLM.vue picks this up on mount to start the
    // async chat-with-document job right away, so its live progress shows in
    // the chat window (DocumentJobProgress) instead of a toast on the home page.
    pendingDocumentChat: null as {
      serverId: string;
      files: { file: File; filename: string }[];
      chunkSize?: number;
      chunkOverlap?: number;
    } | null,
  }),
});
